import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SystemApiService } from '@cv-invision/core';
import { map, Observable } from 'rxjs';
import { QCTimePerUserItem, QCWeekData } from '../model/user-qc-time.model';
import { OrganizationModel } from '../model/organization.model';

@Injectable()
export class ReportsService {
    constructor(private httpClient: HttpClient, private systemApiService: SystemApiService) {}

    /**
     * Returns the user qc metrics from the intelligence api by a range of dates.
     * @param startDate
     * @param endDate
     * @returns {Observable<QCTimePerUserItem[]>}
     */
    public getUserQCTimeItems(
        startDate: string,
        endDate: string,
        organizations: string[],
        products: string[],
    ): Observable<QCTimePerUserItem[]> {
        let params = new HttpParams();
        // Append organizations array
        if (organizations && organizations.length) {
            organizations.forEach((org) => {
                params = params.append('Organization', org);
            });
        }

        if (products && products.length) {
            products.forEach((product) => {
                params = params.append('products', product);
            });
        }

        return this.httpClient
            .get(
                `${this.systemApiService.api['intelligenceService']}reports/performance-per-user/${startDate}/${endDate}`,
                { params },
            )
            .pipe(
                map((payload: any) => {
                    return this.formatUserQCTimeItems(payload.data);
                }),
            );
    }

    /**
     * Returns the weekly performance metrics from the intelligence API by a range of dates.
     * @param startDate
     * @param endDate
     * @returns {Observable<QCWeekData[]>}
     */
    public getPerformancePerWeek(startDate: string, endDate: string): Observable<QCWeekData[]> {
        return this.httpClient.get<QCWeekData[]>(
            `${this.systemApiService.api['intelligenceService']}reports/performance-per-week/${startDate}/${endDate}`,
        );
    }

    /**
     * Returns the user qc metrics CSV FILE from the intelligence api by a range of dates.
     * @param startDate
     * @param endDate
     * @returns {Observable<QCTimePerUserItem[]>}
     */
    public getUserQCTimeItemsCSV(startDate: string, endDate: string) {
        return this.httpClient
            .get(
                `${this.systemApiService.api['intelligenceService']}reports/performance-per-user/${startDate}/${endDate}/csv`,
                {
                    responseType: 'blob',
                },
            )
            .subscribe((blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'performance-per-user.csv';
                link.click();
                window.URL.revokeObjectURL(url);
            });
    }

    private formatUserQCTimeItems(data: any): QCTimePerUserItem[] {
        if (!data?.length) return [];
        return data.map(
            (item: any): QCTimePerUserItem => ({
                classUserId: item.classUserId ?? null,
                fullName: item.fullName ?? null,
                totalReviewed: item.totalReviewed ?? 0,
                totalRejectedByQCReviewer: item.totalRejectedByQCReviewer ?? 0,
                totalRejectedByClient: item.totalRejectedByClient ?? 0,
                avgMinutesWaitingForReview: item.avgMinutesWaitingForReview ?? 0,
                avgMinutesInReview: item.avgMinutesInReview ?? 0,
                avgMinutesActiveWorkInReview: item.avgMinutesActiveWorkInReview ?? 0,
                revisionRate: item.revisionRate ?? 0,
                clientRevisionRate: item.clientRevisionRate ?? 0,
                avgInitialViolationsToCheck: item.avgInitialViolationsToCheck ?? 0,
                avgInitialViolationsFailed: item.avgInitialViolationsFailed ?? 0,
                avgInitialViolationsNotReviewed: item.avgInitialViolationsNotReviewed ?? 0,
                avgMinutesInReviewInitial: item.avgMinutesInReviewInitial ?? 0,
                avgMinutesWaitingForReviewInitial: item.avgMinutesWaitingForReviewInitial ?? 0,
                avgMinutesActiveWorkInReviewInitial: item.avgMinutesActiveWorkInReviewInitial ?? 0
            }),
        );
    }
}
